import {
  Card,
  Grow,
  Zoom,
  Typography,
  Grid,
  Button,
  List,
  Box,
  CardContent,
  CardActions,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Alert,
  Tooltip,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import React, { FC, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../store";
import { fetchedPlayer } from "../../features/player/playerSlice";
import {
  fetchTournoi,
  getTournoi,
} from "../../features/tournoi/tournoiSlice";
import { admin } from "../../features/session/sessionSlice";
import { isPreRegistrationEntryFlow } from "../../utils/registerFlow";
import {
  isBlockedByWomenOnlyTournoi,
  WOMEN_ONLY_BLOCK_MESSAGE,
} from "../../utils/tournoiEligibility";
import {
  Badge,
  Class,
  Groups,
  PermIdentity,
  Stars,
  Wc,
} from "@mui/icons-material";

interface RegisterLicenceValidationProps {}

const RegisterLicenceValidation: FC<RegisterLicenceValidationProps> = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const player = useAppSelector(fetchedPlayer);
  const tournoi = useAppSelector(getTournoi);
  const isAdmin = useAppSelector(admin);
  const isPreRegistrationEntry = isPreRegistrationEntryFlow();
  const entryRoute =
    !isAdmin && isPreRegistrationEntry
      ? "/pre-registration"
      : "/register";
  const blockedByWomenOnly = isBlockedByWomenOnlyTournoi(tournoi, player);

  useEffect(() => {
    void dispatch(fetchTournoi());
  }, [dispatch]);

  useEffect(() => {
    if (player == null) {
      navigate(entryRoute, { replace: true });
    }
  }, [player, navigate, entryRoute]);

  const handleValidate = async () => {
    if (blockedByWomenOnly) {
      return;
    }
    navigate("/register/select");
  };

  if (player == null) {
    return null;
  }

  return (
    <Zoom in>
      <Grid
        container
        direction="column"
        justifyContent="center"
        alignItems="center"
        spacing={2}
        sx={{ width: "100%", maxWidth: 560, mx: "auto", px: 2 }}
      >
        <Grid item>
          <Box display="inline-block">
            {blockedByWomenOnly ? (
              <Alert
                severity="error"
                variant="outlined"
                sx={(t) => ({
                  maxWidth: 480,
                  textAlign: "center",
                  borderLeft: `4px solid ${t.palette.error.main}`,
                })}
              >
                {WOMEN_ONLY_BLOCK_MESSAGE}
              </Alert>
            ) : (
              <Alert
                severity="info"
                variant="standard"
                icon={false}
                sx={(t) => ({
                  border: "1px solid",
                  fontWeight: 500,
                  px: 3,
                  py: 2,
                  boxShadow: 1,
                  maxWidth: 480,
                  textAlign: "center",
                  borderLeft: `4px solid ${t.palette.secondary.main}`,
                  ...(t.palette.mode === "dark"
                    ? {
                        bgcolor: alpha(t.palette.secondary.main, 0.14),
                        borderColor: alpha(t.palette.secondary.light, 0.4),
                        color: t.palette.grey[50],
                      }
                    : {
                        bgcolor: t.palette.secondary.light,
                        borderColor: alpha(t.palette.secondary.main, 0.35),
                        color: t.palette.text.primary,
                      }),
                })}
              >
                Veuillez vérifier que les informations correspondent bien au
                joueur que vous voulez inscrire
              </Alert>
            )}
          </Box>
        </Grid>
        <Grid item>
          <Box display="flex" justifyContent="center" width="100%" px={2}>
            <Grow in>
              <Card
                elevation={4}
                sx={{
                  borderRadius: 3,
                  overflow: "hidden",
                  maxWidth: 480,
                  boxShadow: 3,
                  mb: 2,
                  borderTop: `4px solid ${theme.palette.secondary.main}`,
                }}
              >
                <CardContent sx={{ minWidth: 300 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      fontWeight: 600,
                      mb: 2,
                      color:
                        theme.palette.mode === "dark"
                          ? theme.palette.secondary.light
                          : theme.palette.secondary.dark,
                    }}
                  >
                    🧍 Informations du joueur
                  </Typography>
                  <List>
                    <ListItem
                      sx={(theme) => ({
                        py: 1.5,
                        px: 2,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        "&:nth-of-type(odd)": {
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? alpha(theme.palette.secondary.main, 0.08)
                              : alpha(theme.palette.secondary.main, 0.14),
                        },
                        "&:hover": {
                          bgcolor:
                            theme.palette.mode === "dark"
                              ? alpha(theme.palette.secondary.main, 0.14)
                              : alpha(theme.palette.secondary.main, 0.22),
                        },
                      })}
                    >
                      <ListItemAvatar>
                        <Tooltip title="Nom" placement="top-end" arrow>
                          <Avatar
                            sx={{
                              bgcolor: "secondary.main",
                              color: theme.palette.getContrastText(
                                theme.palette.secondary.main
                              ),
                            }}
                          >
                            <PermIdentity />
                          </Avatar>
                        </Tooltip>
                      </ListItemAvatar>
                      <ListItemText
                        primary={`${player.name} ${player.surname}`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemAvatar>
                        <Tooltip
                          title="Numéro de licence"
                          placement="top-end"
                          arrow
                        >
                          <Avatar
                            sx={{
                              bgcolor: alpha(theme.palette.secondary.main, 0.82),
                              color: theme.palette.getContrastText(
                                theme.palette.secondary.main
                              ),
                            }}
                          >
                            <Badge />
                          </Avatar>
                        </Tooltip>
                      </ListItemAvatar>
                      <ListItemText
                        primaryTypographyProps={{
                          fontWeight: 500,
                          color: "text.primary",
                        }}
                        primary={player.licenceId}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemAvatar>
                        <Tooltip title="Club" placement="top-end" arrow>
                          <Avatar
                            sx={{
                              bgcolor: alpha(theme.palette.secondary.main, 0.72),
                              color: theme.palette.getContrastText(
                                theme.palette.secondary.main
                              ),
                            }}
                          >
                            <Groups />
                          </Avatar>
                        </Tooltip>
                      </ListItemAvatar>
                      <ListItemText primary={player.club} />
                    </ListItem>
                    <ListItem>
                      <ListItemAvatar>
                        <Tooltip
                          title="Points sur la licence"
                          placement="top-end"
                          arrow
                        >
                          <Avatar
                            sx={{
                              bgcolor: alpha(theme.palette.secondary.main, 0.72),
                              color: theme.palette.getContrastText(
                                theme.palette.secondary.main
                              ),
                            }}
                          >
                            <Stars />
                          </Avatar>
                        </Tooltip>
                      </ListItemAvatar>
                      <ListItemText primary={`${player.points} Points`} />
                    </ListItem>
                    <ListItem>
                      <ListItemAvatar>
                        <Tooltip title="Genre" placement="top-end" arrow>
                          <Avatar
                            sx={{
                              bgcolor: alpha(theme.palette.secondary.main, 0.72),
                              color: theme.palette.getContrastText(
                                theme.palette.secondary.main
                              ),
                            }}
                          >
                            <Wc />
                          </Avatar>
                        </Tooltip>
                      </ListItemAvatar>
                      <ListItemText
                        primary={player.sexe === "M" ? "Homme" : "Femme"}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemAvatar>
                        <Tooltip
                          title="Catégorie d'age"
                          placement="top-end"
                          arrow
                        >
                          <Avatar
                            sx={{
                              bgcolor: alpha(theme.palette.secondary.main, 0.72),
                              color: theme.palette.getContrastText(
                                theme.palette.secondary.main
                              ),
                            }}
                          >
                            <Class />
                          </Avatar>
                        </Tooltip>
                      </ListItemAvatar>
                      <ListItemText primary={player.categorie} />
                    </ListItem>
                  </List>
                </CardContent>
                <CardActions>
                  <Grid
                    container
                    alignItems="center"
                    justifyContent="center"
                    sx={{ paddingBottom: "16px" }}
                  >
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={handleValidate}
                      disabled={blockedByWomenOnly}
                      sx={{
                        mt: 2,
                        px: 4,
                        py: 1.5,
                        fontWeight: 600,
                        textTransform: "none",
                        transition: "transform 0.3s ease",
                        "&:hover": { transform: "translateY(-2px)" },
                      }}
                    >
                      Il s'agit bien de moi
                    </Button>
                  </Grid>
                </CardActions>
              </Card>
            </Grow>
          </Box>
        </Grid>
      </Grid>
    </Zoom>
  );
};

export default RegisterLicenceValidation;
